import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  LoadingRow,
  PaginationComponent,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@folio/components';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  branchControllerInvalidateBranch,
  legacyPermissionsControllerAtMeQueryOptions,
  type ListBrokerageFirmPoliciesInputBody,
  type PolicyBranchesForBrokerEntity,
  type PolicyDtoStatusEnumKey,
  viewSpecificControllerListBrokerageFirmPoliciesQueryOptions,
} from 'korint-sdk';
import {
  AWAITING_FIRST_PAYMENT,
  DEROGATION_APPROVED,
  STOP_AWAITING_VALIDATION,
  STOP_SCHEDULED,
  type FilterConfig,
  type PolicyColumnConfig,
  type SelectFilterConfig,
  useKorintConfig,
} from 'korint-sdk/react';
import { ArchiveIcon, ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getDisplayedPolicyStatus, getPolicyCustomStatusKey } from '../../features/PolicyStatus';
import { usePolicyStatuses } from '../../../hooks/usePolicyStatuses';
import { NoResultsRow } from './NoResultsRow';
import { PolicyRow } from './PolicyRow';
import SearchAndFilters, { type Filter, type FilterField } from './SearchAndFilters';
import { cloneDeep } from 'lodash';
import { Group } from '@folio/components/ui/flex';

const PAGE_SIZE = 10;

const PENDING_STOP_STATE_BY_STATUS = {
  [STOP_SCHEDULED]: 'SCHEDULED',
  [STOP_AWAITING_VALIDATION]: 'AWAITING_VALIDATION',
} as const;

// Display states resolved to their own filter flag, never sent as a PolicyStatus.
const PSEUDO_STATUSES: string[] = [
  ...Object.keys(PENDING_STOP_STATE_BY_STATUS),
  AWAITING_FIRST_PAYMENT,
  DEROGATION_APPROVED,
];

// ponytail: STOP_SCHEDULED was TERMINATION_SCHEDULED until ENG-4954, and a shared or bookmarked
// ?filters= URL still carries the old spelling. Canonicalised where the URL is parsed so the
// chip, the row filtering and the query all agree. Drop once no saved URL can plausibly hold it.
const canonicalStatus = (value: string) =>
  value === 'TERMINATION_SCHEDULED' ? STOP_SCHEDULED : value;

export const canonicalizeFilters = (filters: Filter[]): Filter[] =>
  filters.map(filter =>
    Array.isArray(filter.value) ? { ...filter, value: filter.value.map(canonicalStatus) } : filter,
  );

export function queryBuilder({
  search,
  filters,
  baseFilters,
  filtersConfig,
}: {
  search?: string;
  filters: Filter[];
  baseFilters: ListBrokerageFirmPoliciesInputBody;
  filtersConfig: Record<string, FilterConfig>;
  expandCustomers?: boolean;
  expandAssets?: boolean;
  expandBrokerageFirm?: boolean;
}): ListBrokerageFirmPoliciesInputBody {
  const inputBody: ListBrokerageFirmPoliciesInputBody = cloneDeep(baseFilters);

  if (search !== undefined) {
    inputBody.queryString = search;
  }

  const toValueArray = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value) || value.length === 0) return undefined;
    return value as string[];
  };

  // Neither pending-stop state is a backend status: both resolve to an indexed pending-stop row
  // (view-specific.service), wherever they appear — policyStatus or policyCustomStatus.
  const selectedStatuses = filters.flatMap(({ value }) =>
    Array.isArray(value) ? (value as string[]) : [],
  );
  const pendingStopStates = Object.entries(PENDING_STOP_STATE_BY_STATUS)
    .filter(([status]) => selectedStatuses.includes(status))
    .map(([, state]) => state);

  if (pendingStopStates.length > 0) {
    inputBody.pendingStopStates = pendingStopStates;
  }

  for (const { field, value } of filters) {
    if (field === 'businessProvider') {
      const values = toValueArray(value);
      if (values) {
        inputBody.businessIntroducers = values;
      }
    }

    if (field === 'policyStatus') {
      const selected = toValueArray(value) ?? [];
      // Every pseudo-status OR-composes backend-side: combined with a real status the user means
      // the union, and an intersection would resolve to an empty list (view-specific.service).
      if (selected.includes(AWAITING_FIRST_PAYMENT)) {
        inputBody.awaitingFirstPayment = true;
      } else if (selected.includes('POLICY_SIGNED')) {
        inputBody.awaitingFirstPayment = false;
      }
      if (selected.includes(DEROGATION_APPROVED)) {
        inputBody.derogationApproved = true;
      }
      const statuses = selected.filter(v => !PSEUDO_STATUSES.includes(v));
      if (statuses.length > 0) {
        inputBody.statuses = statuses as ListBrokerageFirmPoliciesInputBody['statuses'];
      }
    }

    if (field === 'policyCustomStatus') {
      const selected = toValueArray(value) ?? [];
      if (selected.includes(AWAITING_FIRST_PAYMENT)) {
        inputBody.awaitingFirstPayment = true;
      } else if (selected.length > 0) {
        inputBody.awaitingFirstPayment = false;
      }
      const values = selected.filter(v => !PSEUDO_STATUSES.includes(v));
      const correlation =
        filtersConfig[field]?.type === 'select'
          ? (filtersConfig[field] as SelectFilterConfig).onSameBranchWith
          : undefined;
      if (values?.length && correlation) {
        inputBody.onSameBranch = [
          { property: 'customFields.customStatus', value: { in: values } },
          correlation,
        ];
      } else if (values?.length) {
        inputBody.customStatuses = values;
      }
    }

    if (field === 'endDate') {
      if (typeof value === 'object' && value !== null) {
        const endedAtRange: { start?: string; end?: string } = {};

        if ('start' in value && value.start) {
          endedAtRange.start = new Date(value.start).toISOString();
        }
        if ('end' in value && value.end) {
          endedAtRange.end = new Date(
            new Date(value.end).setUTCHours(23, 59, 59, 999),
          ).toISOString();
        }

        inputBody.endedAt = endedAtRange;
      }
    }

    if (field === 'creationDate') {
      if (typeof value === 'object' && value !== null) {
        const createdAtRange: { start?: string; end?: string } = {};

        if ('start' in value && value.start) {
          createdAtRange.start = new Date(value.start).toISOString();
        }
        if ('end' in value && value.end) {
          createdAtRange.end = new Date(
            new Date(value.end).setUTCHours(23, 59, 59, 999),
          ).toISOString();
        }

        inputBody.createdAt = createdAtRange;
      }
    }

    if (field === 'startedAt') {
      if (typeof value === 'object' && value !== null) {
        const startedAtRange: { start?: string; end?: string } = {};

        if ('start' in value && value.start) {
          startedAtRange.start = new Date(value.start).toISOString();
        }
        if ('end' in value && value.end) {
          startedAtRange.end = new Date(
            new Date(value.end).setUTCHours(23, 59, 59, 999),
          ).toISOString();
        }

        inputBody.startedAt = startedAtRange;
      }
    }

    if (field === 'complianceAlerts') {
      inputBody.hasAcknowledgedAlerts = !value;
    }

    if (field === 'productIds') {
      const values = toValueArray(value);
      if (values) {
        inputBody.productIds = values;
      }
    }

    if (field === 'brokerageFirmName') {
      if (typeof value === 'string' && value.trim().length > 0) {
        inputBody.brokerageFirmName = value.trim();
      }
    }

    if (field === 'stopReason') {
      const values = toValueArray(value);
      if (values) {
        inputBody.stopReasons = values;
      }
    }

    if (field === 'assignedBroker') {
      const values = toValueArray(value);
      if (values) {
        inputBody.authorUserIds = values;
      }
    }

    if (field === 'insurer') {
      const values = toValueArray(value);
      if (values) {
        inputBody.insurers = values as ListBrokerageFirmPoliciesInputBody['insurers'];
      }
    }

    if (field === 'fraudStatus') {
      const values = toValueArray(value);
      if (values) {
        inputBody.fraudStatuses = values as ListBrokerageFirmPoliciesInputBody['fraudStatuses'];
      }
    }
  }

  return inputBody;
}

type DisplayedStatusSelection = {
  field: 'policyStatus' | 'policyCustomStatus';
  selected: ReadonlyArray<string>;
  options: ReadonlyArray<string>;
};

/**
 * New-business views return every open branch of a matching policy, and some display states
 * (an approved derogation) are inexpressible in the search DSL — so drop the branches whose pill
 * doesn't match the selection here, where the pill is computed.
 */
export function filterBranchesByDisplayedStatus({
  branches,
  selection,
  getPolicyStatusOverride,
  policyStatuses,
}: {
  branches: PolicyBranchesForBrokerEntity[];
  selection: DisplayedStatusSelection;
  getPolicyStatusOverride?: (
    status: PolicyDtoStatusEnumKey,
    stopReason?: string,
  ) => string | undefined;
  policyStatuses: Record<string, string>;
}): PolicyBranchesForBrokerEntity[] {
  const selected = new Set(selection.selected);

  return branches.filter(({ policy, branch }) => {
    const displayed = getDisplayedPolicyStatus({
      policy,
      branchDerogations: branch?.derogations,
      getPolicyStatusOverride,
      policyStatuses,
    });

    if (selected.has(displayed)) {
      return true;
    }

    // A display state the filter carries no option for would become unreachable; keep it findable
    // under its raw status instead. States the filter can express stay strict.
    if (selection.options.includes(displayed)) {
      return false;
    }

    const nativeStatus =
      selection.field === 'policyCustomStatus'
        ? getPolicyCustomStatusKey(policy.customFields)
        : policy.status;

    return nativeStatus !== undefined && selected.has(nativeStatus);
  });
}

interface PolicyTableProps {
  title?: string;
  expandCustomers?: boolean;
  expandAssets?: boolean;
  expandBrokerageFirm?: boolean;
  filtersConfig: Record<string, FilterConfig>;
  baseFilters: ListBrokerageFirmPoliciesInputBody;
  columns?: ReadonlyArray<PolicyColumnConfig>;
  initialFilters?: Filter[];
  selectable?: boolean;
  policyLinkSuffix?: string;
}

export function PolicyTable({
  title,
  filtersConfig,
  baseFilters,
  expandCustomers,
  expandAssets,
  expandBrokerageFirm,
  columns = [],
  initialFilters,
  selectable = false,
  policyLinkSuffix,
}: PolicyTableProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { korintConfig } = useKorintConfig();
  const policyStatuses = usePolicyStatuses();
  const { data: { brokerageFirmId } = {} } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const [selectedPolicyIds, setSelectedPolicyIds] = useState<Set<string>>(new Set());

  const [searchParams, setSearchParams] = useSearchParams();

  const allFields = Object.keys(filtersConfig) as FilterField[];

  const filters = useMemo(() => {
    const filtersParam = searchParams.get('filters');

    try {
      if (filtersParam) {
        // Unknown fields (stale URLs) have no filtersConfig entry and crash the value renderer.
        return canonicalizeFilters(
          (JSON.parse(filtersParam) as Filter[]).filter(f => filtersConfig[f.field]),
        );
      }
    } catch (error) {
      // ignore and fall through to return default
    }

    return initialFilters && initialFilters.length > 0
      ? initialFilters
      : [{ field: allFields[0], value: null }];
  }, [searchParams, initialFilters, allFields]);

  const setFilters = useCallback(
    (newFilters: Filter[] | ((prev: Filter[]) => Filter[])) => {
      setSearchParams(prev => {
        const nextFilters = typeof newFilters === 'function' ? newFilters(filters) : newFilters;
        prev.set('filters', JSON.stringify(nextFilters));
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams, filters],
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(prev => {
        prev.set('search', value);
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const search = searchParams.get('search') ?? '';

  const currentPage = useMemo(() => {
    const page = Number.parseInt(searchParams.get('page') ?? '', 10);
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }, [searchParams]);

  const orderBy = searchParams.get('orderBy') ?? '';
  const orderDirection = (searchParams.get('orderDirection') ?? 'asc') as 'asc' | 'desc';

  const handleSort = useCallback(
    (columnName: string) => {
      setSearchParams(prev => {
        const currentOrderBy = prev.get('orderBy');
        const currentOrderDirection = prev.get('orderDirection') ?? 'asc';

        if (currentOrderBy === columnName) {
          // Three-state cycle: asc -> desc -> no sort -> asc
          if (currentOrderDirection === 'asc') {
            prev.set('orderDirection', 'desc');
          } else if (currentOrderDirection === 'desc') {
            // Remove sorting (reset to default)
            prev.delete('orderBy');
            prev.delete('orderDirection');
          }
        } else {
          // Set new column and default to ascending
          prev.set('orderBy', columnName);
          prev.set('orderDirection', 'asc');
        }
        prev.set('page', '1'); // Reset to first page when sorting
        return prev;
      });
    },
    [setSearchParams],
  );

  const queryOptions = viewSpecificControllerListBrokerageFirmPoliciesQueryOptions(
    brokerageFirmId ?? '',
    {
      ...queryBuilder({
        search,
        filters,
        baseFilters,
        filtersConfig,
      }),
      expandCustomers,
      expandAssets,
      expandBrokerageFirm,
      pageInfo: { page: currentPage, count: PAGE_SIZE },
      ...(orderBy &&
        orderDirection && {
          order: {
            by: orderBy === 'name' ? 'customerName' : orderBy,
            direction: orderDirection,
          },
        }),
    },
  );

  const {
    data: policies,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    ...queryOptions,
    placeholderData: keepPreviousData,
  });

  const displayedStatusSelection = useMemo((): DisplayedStatusSelection | undefined => {
    if (baseFilters.viewType !== 'newBusiness' && baseFilters.viewType !== 'archivedNewBusiness') {
      return undefined;
    }

    for (const { field, value } of filters) {
      if (field !== 'policyStatus' && field !== 'policyCustomStatus') continue;
      if (!Array.isArray(value) || value.length === 0) continue;

      const config = filtersConfig[field];
      return {
        field,
        selected: value as string[],
        options: config?.type === 'select' ? config.options : [],
      };
    }

    return undefined;
  }, [baseFilters.viewType, filters, filtersConfig]);

  const displayData = useMemo(() => {
    if (!policies?.data || !displayedStatusSelection) return policies?.data;

    return policies.data
      .map(policy => ({
        ...policy,
        branches: filterBranchesByDisplayedStatus({
          branches: policy.branches,
          selection: displayedStatusSelection,
          getPolicyStatusOverride: korintConfig.getPolicyStatusOverride,
          policyStatuses,
        }),
      }))
      .filter(({ branches }) => branches.length > 0);
  }, [
    policies?.data,
    displayedStatusSelection,
    korintConfig.getPolicyStatusOverride,
    policyStatuses,
  ]);

  const togglePolicySelection = useCallback((policyId: string) => {
    setSelectedPolicyIds(prev => {
      const next = new Set(prev);
      if (next.has(policyId)) {
        next.delete(policyId);
      } else {
        next.add(policyId);
      }
      return next;
    });
  }, []);

  const currentPagePolicyIds = useMemo(
    () => displayData?.map(({ policyId }) => policyId) ?? [],
    [displayData],
  );

  const allPageSelected =
    currentPagePolicyIds.length > 0 && currentPagePolicyIds.every(id => selectedPolicyIds.has(id));

  const somePageSelected =
    !allPageSelected && currentPagePolicyIds.some(id => selectedPolicyIds.has(id));

  const toggleSelectAll = useCallback(() => {
    setSelectedPolicyIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const id of currentPagePolicyIds) next.delete(id);
      } else {
        for (const id of currentPagePolicyIds) next.add(id);
      }
      return next;
    });
  }, [allPageSelected, currentPagePolicyIds]);

  const { mutate: archiveSelected, isPending: isArchiving } = useMutation({
    mutationFn: async (policyIds: string[]) => {
      const callsByPolicy = new Map<string, Promise<unknown>[]>();
      for (const policyId of policyIds) {
        const policyData = policies?.data.find(p => p.policyId === policyId);
        if (!policyData) continue;
        const calls: Promise<unknown>[] = [];
        for (const { branch, policy } of policyData.branches) {
          if (branch?.branchId) {
            calls.push(
              branchControllerInvalidateBranch(policy.id, branch.branchId, {
                reason: 'BROKER_REQUEST',
              }),
            );
          }
        }
        if (calls.length > 0) {
          callsByPolicy.set(policyId, calls);
        }
      }

      const failedPolicyIds: string[] = [];
      await Promise.all(
        Array.from(callsByPolicy.entries()).map(async ([policyId, calls]) => {
          const results = await Promise.allSettled(calls);
          if (results.some(r => r.status === 'rejected')) {
            failedPolicyIds.push(policyId);
          }
        }),
      );

      return { failedPolicyIds };
    },
    onSuccess: ({ failedPolicyIds }) => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      if (failedPolicyIds.length === 0) {
        setSelectedPolicyIds(new Set());
        toast({ variant: 'default', title: t('policyList.archiveSuccess') });
      } else {
        setSelectedPolicyIds(new Set(failedPolicyIds));
        toast({ variant: 'destructive', title: t('policyList.archivePartialError') });
      }
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      toast({ variant: 'destructive', title: t('policyList.archiveError') });
    },
  });

  return (
    <Card>
      {title && (
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <Group align="start" gap={2}>
          <div className="flex-1">
            <SearchAndFilters
              search={search}
              setSearch={setSearch}
              filters={filters}
              setFilters={setFilters}
              filtersConfig={filtersConfig}
              initialFilters={initialFilters}
            />
          </div>
          {selectable && (
            <Button
              className="mb-4 shrink-0 min-w-48"
              disabled={selectedPolicyIds.size === 0 || isArchiving}
              loading={isArchiving}
              onClick={() => archiveSelected(Array.from(selectedPolicyIds))}
              leftIcon={<ArchiveIcon className="h-4 w-4" />}
            >
              {t('policyList.archivePolicies', { count: selectedPolicyIds.size })}
            </Button>
          )}
        </Group>
        <Table>
          <TableHeader>
            <TableRow>
              {selectable && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={somePageSelected ? 'indeterminate' : allPageSelected}
                    onCheckedChange={toggleSelectAll}
                    disabled={currentPagePolicyIds.length === 0}
                  />
                </TableHead>
              )}
              <TableHead />
              {columns.map(({ name }) => {
                const isSortable = ['name', 'createdAt', 'signedAt'].indexOf(name) !== -1;
                const isCurrentSort = orderBy === name;
                const columnLabel = t(`policyList.columns.${name}`, name);

                return (
                  <TableHead key={name}>
                    {isSortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(name)}
                        className="flex items-center gap-1 hover:text-foreground transition-colors"
                        title={`Click to sort ${isCurrentSort ? (orderDirection === 'asc' ? 'descending' : 'remove sort') : 'ascending'}`}
                      >
                        {columnLabel}
                        {isCurrentSort ? (
                          orderDirection === 'asc' ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 opacity-50" />
                        )}
                      </button>
                    ) : (
                      columnLabel
                    )}
                  </TableHead>
                );
              })}
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading || isFetching ? (
              // Show skeleton loading rows
              Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <LoadingRow key={`loading-${index}`} />
              ))
            ) : !displayData || displayData.length === 0 ? (
              <NoResultsRow
                isSearchPoliciesLoading={isLoading}
                isSearchPoliciesError={isError}
                pageSize={PAGE_SIZE}
              />
            ) : (
              displayData.map(policy => (
                <PolicyRow
                  key={policy.policyId}
                  branches={policy.branches}
                  columns={columns}
                  selectable={selectable}
                  selected={selectedPolicyIds.has(policy.policyId)}
                  onToggleSelect={() => togglePolicySelection(policy.policyId)}
                  policyLinkSuffix={policyLinkSuffix}
                />
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <PaginationComponent
          totalData={policies?.totalCount ?? 0}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={isLoading || isFetching}
        />
      </CardFooter>
    </Card>
  );
}
